/**
 * Camera geometry — one calibrated number, everything else derived.
 *
 * ## What is stored
 *
 * The **diagonal** field of view, because that is what phone manufacturers
 * publish ("field of view 76°") and therefore the only number a user can look up
 * rather than guess. Horizontal and vertical are derived from it and the actual
 * frame aspect.
 *
 * ## Why this is not just one angle
 *
 * Three separate things need different answers, and conflating them unsticks the
 * world even when tracking is perfect:
 *
 * 1. **The tracker** sees the whole captured frame, so it needs the frame's
 *    true focal length — expressed in whatever angle AlvaAR's `fov` argument
 *    means for that buffer's orientation (see `trackerFovDeg`).
 * 2. **The renderer's horizontal axis** does NOT, because `.passthrough` uses
 *    `object-fit: cover`. A 3:4 video on a 9:19.5 phone screen gets ~39% of its
 *    width cropped away — the participant sees far less horizontally than the
 *    camera captured.
 * 3. **The renderer's vertical axis** is different again: 3:4 portrait has a much
 *    wider vertical FOV than horizontal (64° vs 50° on the reference device).
 *
 * The previous code used a single angle for all three. On an Oppo (360×791 CSS
 * px, 480×640 capture, 76° diagonal) that made the horizontal mapping ~57% too
 * wide and the vertical ~22% too narrow, in opposite directions. Fragments
 * therefore slid one way when panning and the other way when tilting.
 *
 * ## Tangent, not linear
 *
 * A pinhole camera maps `tan(angle)`, not angle. The old linear approximation is
 * fine near the centre but is ~8% off at the edge of a 64° vertical field. Since
 * fragments are meant to sit on real objects, that error is exactly the visible
 * kind.
 */

const KEY = 'wintermute.cameraDiagonalFovDeg';

/**
 * Diagonal FOV in degrees.
 *
 * 76 is the reference device (Oppo main camera, published spec). Typical phone
 * main cameras are 70-80 diagonal; ultrawides are 100-120. If the browser picks
 * an ultrawide, expect to need a much larger value.
 */
export const DEFAULT_DIAGONAL_FOV_DEG = 76;
export const MIN_DIAGONAL_FOV_DEG = 40;
export const MAX_DIAGONAL_FOV_DEG = 140;

function load(): number {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw === null) return DEFAULT_DIAGONAL_FOV_DEG;
    const v = Number(raw);
    if (!Number.isFinite(v) || v < MIN_DIAGONAL_FOV_DEG || v > MAX_DIAGONAL_FOV_DEG) {
      return DEFAULT_DIAGONAL_FOV_DEG;
    }
    return v;
  } catch {
    return DEFAULT_DIAGONAL_FOV_DEG;
  }
}

let diagonalFov = load();

export function cameraDiagonalFovDeg(): number {
  return diagonalFov;
}

/** Set and persist. Returns the clamped value actually applied. */
export function setCameraDiagonalFovDeg(deg: number): number {
  if (!Number.isFinite(deg)) return diagonalFov;
  diagonalFov = Math.max(MIN_DIAGONAL_FOV_DEG, Math.min(MAX_DIAGONAL_FOV_DEG, deg));
  try {
    localStorage.setItem(KEY, String(diagonalFov));
  } catch {
    /* not persisting is survivable */
  }
  return diagonalFov;
}

const DEG = Math.PI / 180;

/**
 * Focal length in pixels for a frame of the given size, from the diagonal FOV.
 * This is the one quantity everything else comes from.
 */
export function focalLengthPx(frameW: number, frameH: number, diagFovDeg = diagonalFov): number {
  const diag = Math.hypot(frameW, frameH);
  return diag / 2 / Math.tan((diagFovDeg * DEG) / 2);
}

/**
 * Full horizontal FOV of the captured frame, in degrees.
 *
 * What the tracker effectively SEES. For display and comparison; this is not
 * necessarily the number to hand AlvaAR — see `trackerFovDeg`.
 */
export function frameHorizontalFovDeg(frameW: number, frameH: number, diagFovDeg = diagonalFov): number {
  const f = focalLengthPx(frameW, frameH, diagFovDeg);
  return (2 * Math.atan(frameW / 2 / f)) / DEG;
}

/**
 * The `fov` argument to pass to `AlvaAR.Initialize(w, h, fov)` so that its
 * focal length comes out equal to the TRUE focal length of the frame.
 *
 * AlvaAR's `getCameraIntrinsics` (client/public/vendor/alva_ar.js, read, not
 * guessed) does:
 *
 *     if (width > height) { fovH = fov * aspect; fovV = fov; }
 *     else                { fovH = fov;          fovV = fov * aspect; }
 *     fx = (w/2) / tan(fovH/2);  fy = (h/2) / tan(fovV/2);  f = min(fx, fy)
 *
 * and assigns `f` to BOTH axes. So:
 *
 *  - **Portrait / square** (`w <= h`): `fov` is the horizontal angle, and
 *    `fx` is the true focal length. Since `tan(a·θ) <= a·tan(θ)` for `a < 1`,
 *    `fy >= fx` and `min` keeps `fx`. Pass the true horizontal FOV.
 *  - **Landscape** (`w > h`): `fov` is read as the VERTICAL angle and the
 *    horizontal one is derived by LINEARLY scaling the angle by the aspect,
 *    which overstates it. Passing the true horizontal angle here — what this
 *    function used to do for every buffer — made `fovH = 64°·4/3 = 85°` on a
 *    640x480 buffer at 76° diagonal, i.e. `f = 348 px` against a true 512 px:
 *    **32% short**. Passing the true vertical angle is not right either: then
 *    `fy` is exact but `fx` (from the overstated linear `fovH`) is smaller, and
 *    `min` picks it. What makes `min(fx, fy)` exact is `fovH = true horizontal`,
 *    i.e. `fov = trueHorizontal / aspect`; `fy` then exceeds `fx` by the same
 *    convexity argument, and `min` keeps the exact `fx`.
 *
 * Checked against the vendor's own `getCameraIntrinsics` in
 * client/test/cameraFov.test.ts, for both orientations.
 */
export function trackerFovDeg(frameW: number, frameH: number, diagFovDeg = diagonalFov): number {
  const h = frameHorizontalFovDeg(frameW, frameH, diagFovDeg);
  if (frameW > frameH) return h / (frameW / frameH);
  return h;
}

export interface ViewGeometry {
  /** tan of the half-angle spanned by the element's width. */
  tanHalfX: number;
  /** tan of the half-angle spanned by the element's height. */
  tanHalfY: number;
  /** Degrees, for display only. */
  hFovDeg: number;
  vFovDeg: number;
}

/**
 * Geometry of a WHOLE captured frame (no `object-fit` crop), from the
 * calibrated diagonal at the moment of the call.
 *
 * This is what the tracker sees: normalised image coordinates from a feature
 * tracker, or from the Watcher's bounding box (normalised against the full
 * captured frame), map through this — not through the renderer's cropped view.
 */
export function frameGeometry(frameW: number, frameH: number, diagFovDeg = diagonalFov): ViewGeometry {
  const f = focalLengthPx(frameW, frameH, diagFovDeg);
  const halfW = frameW / 2;
  const halfH = frameH / 2;
  return {
    tanHalfX: halfW / f,
    tanHalfY: halfH / f,
    hFovDeg: (2 * Math.atan(halfW / f)) / DEG,
    vFovDeg: (2 * Math.atan(halfH / f)) / DEG,
  };
}

/** Assumed frame when nothing better is known: 3:4 portrait, the reference device. */
const FALLBACK_FRAME = { width: 480, height: 640 };

/**
 * What the participant can actually SEE, accounting for `object-fit: cover`.
 *
 * cover scales the video by max(elemW/videoW, elemH/videoH) and centre-crops the
 * overflow, so on a tall phone screen a 3:4 frame loses a large slice of its
 * width. Fragments must be projected against the visible extent, not the
 * captured one, or they drift horizontally as soon as the participant turns.
 */
export function viewGeometry(
  video: HTMLVideoElement | null,
  fallbackFrame?: { width: number; height: number } | null,
): ViewGeometry {
  const vw = video?.videoWidth ?? 0;
  const vh = video?.videoHeight ?? 0;
  const ew = video?.clientWidth ?? 0;
  const eh = video?.clientHeight ?? 0;
  if (vw <= 0 || vh <= 0 || ew <= 0 || eh <= 0) {
    // No usable element. Use the caller's real frame size when it has one, and
    // ALWAYS the current calibrated diagonal: this used to be a constant built
    // at module load from DEFAULT_DIAGONAL_FOV_DEG, so a user's FOV calibration
    // and the actual frame aspect were both silently ignored by every caller
    // that passed null.
    const fb =
      fallbackFrame && fallbackFrame.width > 0 && fallbackFrame.height > 0
        ? fallbackFrame
        : FALLBACK_FRAME;
    return frameGeometry(fb.width, fb.height);
  }

  const f = focalLengthPx(vw, vh);
  const scale = Math.max(ew / vw, eh / vh); // object-fit: cover
  // Visible half-extent of the frame, in source pixels. Clamped because a
  // letterboxed element (object-fit: contain) would otherwise report more of the
  // frame than exists.
  const halfW = Math.min(vw / 2, ew / 2 / scale);
  const halfH = Math.min(vh / 2, eh / 2 / scale);

  return {
    tanHalfX: halfW / f,
    tanHalfY: halfH / f,
    hFovDeg: (2 * Math.atan(halfW / f)) / DEG,
    vFovDeg: (2 * Math.atan(halfH / f)) / DEG,
  };
}
